import { Expose, Type, Transform } from 'class-transformer';

import { Default } from '@lib/common/decorators';
import { SnModel, SnCollection } from '@lib/common/models';
import { ISnIsSharable } from '@lib/share';

import { SnContentType, IHasContentType } from './base';
import { SnCamera } from './camera';
import { FususRegionCode } from './constants';
import { SnOrganization } from './organization';
import { SnShareInfo, SnShareDirection, SnSharePermissions } from './share';
import { SnCameraStats } from './stats';
import { SnTag } from './tag';
import { SnUser } from './user';

export class SnCameraGrid extends SnModel implements ISnIsSharable, IHasContentType {
  @Expose()
  public id: string;

  @Expose()
  public name: string;

  @Expose()
  public createdBy: SnUser;

  @Expose()
  public updatedBy: SnUser;

  @Expose()
  public updatedTimestamp: Date;

  @Expose()
  @Default(true)
  public autoplay: boolean;

  @Expose()
  @Default(true)
  public isPrivate: boolean;

  @Expose()
  @Type(() => SnCameraStats)
  public readonly stats: SnCameraStats;

  @Expose()
  @Type(() => SnOrganization)
  public organization: SnOrganization;

  @Expose()
  public isShared: boolean;

  @Expose()
  public region?: FususRegionCode;

  @Expose()
  @Default(false)
  public isTemporary: boolean;

  @Expose()
  @Type(() => SnShareInfo)
  public shareInfo: SnShareInfo;

  @Expose()
  @Type(() => SnTag)
  @Default(new SnCollection())
  public tags: SnCollection<SnTag>;

  @Expose()
  @Type(() => SnCamera)
  @Default(new SnCollection())
  public readonly cameras: SnCollection<SnCamera>;

  public geometry: { cols: number; rows: number };
  public metaData: any;

  public readonly contentType = SnContentType.Grid;

  constructor(data: any = {}) {
    super();

    this.fromJSON(data);
  }

  @Expose()
  @Transform((value, data) => data.meta)
  public set meta(value: any) {
    this.metaData = value;
    this.geometry =
      value && value.hasOwnProperty('geometry') ? value.geometry : { cols: 3, rows: 3 };

    if (value && value.hasOwnProperty('order')) {
      this._sortCamerasByOrder(value.order);
    }
  }

  get canEdit(): boolean {
    return !this.isShared || this.shareInfo.permissions === SnSharePermissions.Edit;
  }

  get isOwned(): boolean {
    return !this.isShared || this.shareInfo.direction === 'Out';
  }

  get isRestricted(): boolean {
    return (
      this.isShared &&
      this.shareInfo.direction === SnShareDirection.In &&
      this.shareInfo.isRestricted
    );
  }

  private _sortCamerasByOrder(orderIds: string[] = []): void {
    const cameras = orderIds.reduce((acc, id) => {
      if (this.cameras.has(id)) {
        acc.push(this.cameras.get(id));
      }

      return acc;
    }, []);

    this.cameras.clear();
    this.cameras.set(...cameras);
  }
}
